export default function MemoryGame(data, cards, onCardMatch, onGameComplete, onCardsShowTimeout) {
    
    let conf = {
        rows: data.rows || 4,
        cols: data.cols || 3,
        revealTime: data.revealTime || 3, // seconds
        cards: cards,
        flippedCards: [],
        matchedPairs: 0,
        totalPairs: Math.floor((data.rows * data.cols) / 2),
        gameStarted: false,
        canFlip: true,
        onCardMatch: onCardMatch,
        onGameComplete: onGameComplete,
        onCardsShowTimeout: onCardsShowTimeout
    };

    const game = {
        
        /**
         * Flip a card at the given index
         * @param {number} index - Index of the card to flip
         */
        flipCard(index) {
            if (!conf.canFlip || conf.cards[index].isFlipped || conf.cards[index].isMatched) {
                return false;
            }

            // Flip the card
            conf.cards[index].isFlipped = true;
            conf.flippedCards.push(index);

            // If two cards are flipped, check for match
            if (conf.flippedCards.length === 2) {
                conf.canFlip = false;
                setTimeout(() => {
                    this.checkMatch();
                }, 250); // Small delay to show the second card
            }

            return true;
        },

        /**
         * Check if the two flipped cards match
         */
        checkMatch() {
            if (conf.flippedCards.length !== 2) {
                return;
            }

            const [firstIndex, secondIndex] = conf.flippedCards;
            const firstCard = conf.cards[firstIndex];
            const secondCard = conf.cards[secondIndex];

            // Check if cards match by comparing pairId
            if (firstCard.pairId === secondCard.pairId) {
                // Match found!
                firstCard.isMatched = true;
                secondCard.isMatched = true;
                conf.matchedPairs++;

                // Notify about the match
                conf.onCardMatch([firstIndex, secondIndex]);

                // Check if game is complete
                if (conf.matchedPairs === conf.totalPairs) {
                    setTimeout(() => {
                        conf.onGameComplete();
                    }, 250); // Small delay for better UX
                }
            } else {
                // No match - flip cards back after a delay
                conf.onCardsShowTimeout();
            }

            // Reset flipped cards and allow flipping again
            conf.flippedCards = [];
            conf.canFlip = true;
        },

        /**
         * Called when the initial card reveal period ends
         */
        onCardsHidden() {
            conf.gameStarted = true;
            conf.canFlip = true;
        },

        /**
         * Reset the game to initial state
         */
        reset() {
            conf.flippedCards = [];
            conf.matchedPairs = 0;
            conf.gameStarted = false;
            conf.canFlip = true;
            
            // Reset all cards
            conf.cards.forEach(card => {
                card.isFlipped = false;
                card.isMatched = false;
            });
        },

        /**
         * Get current game statistics
         */
        getStats() {
            return {
                matchedPairs: conf.matchedPairs,
                totalPairs: conf.totalPairs,
                flippedCards: conf.flippedCards.length,
                gameStarted: conf.gameStarted,
                canFlip: conf.canFlip,
                isComplete: conf.matchedPairs === conf.totalPairs
            };
        },

        /**
         * Get configuration
         */
        getConfig() {
            return { ...conf };
        }
    };

    return game;
}

/**
 * Memory Game Utility Functions
 */

/**
 * Shuffle an array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} - Shuffled array
 */
export function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Create card pairs for memory game
 * @param {Array} items - Array of image items
 * @param {number} totalCards - Total number of cards needed
 * @returns {Array} - Array of card pairs
 */
export function createCardPairs(items, totalCards) {
    const totalPairs = Math.floor(totalCards / 2);
    let cardPairs = [];
    
    for (let i = 0; i < totalPairs; i++) {
        const item = items[i % items.length];
        const cardData = {
            imageUrl: item.imageUrl,
            altText: item.altText || `Memory card ${i + 1}`,
            pairId: i,
            isFlipped: false,
            isMatched: false
        };
        
        // Add two identical cards for each pair
        cardPairs.push({ ...cardData }, { ...cardData });
    }
    
    return shuffleArray(cardPairs);
}

/**
 * Calculate optimal card size based on screen dimensions
 * @param {number} screenWidth - Available screen width
 * @param {number} screenHeight - Available screen height
 * @param {number} rows - Number of rows
 * @param {number} cols - Number of columns
 * @param {number} margin - Margin around the grid
 * @param {number} gap - Gap between cards
 * @returns {Object} - Card dimensions and grid info
 */
export function calculateCardDimensions(screenWidth, screenHeight, rows, cols, margin = 30, gap = 10) {
    const availableWidth = screenWidth - margin;
    const availableHeight = screenHeight - margin - 200; // Reserve space for buttons and other UI
    
    const totalGapWidth = gap * (cols - 1);
    const totalGapHeight = gap * (rows - 1);
    
    const maxCardWidth = Math.floor((availableWidth - totalGapWidth) / cols);
    const maxCardHeight = Math.floor((availableHeight - totalGapHeight) / rows);
    
    // Keep cards square, use the smaller dimension
    const cardSize = Math.min(maxCardWidth, maxCardHeight, 150); // Max 150px for large screens
    
    const gridWidth = (cardSize * cols) + totalGapWidth;
    const gridHeight = (cardSize * rows) + totalGapHeight;
    
    return {
        cardWidth: cardSize,
        cardHeight: cardSize,
        gridWidth,
        gridHeight,
        gap
    };
}

/**
 * Memory Game Animation Helpers
 */

/**
 * Animate card flip with CSS transitions
 * @param {HTMLElement} cardElement - The card DOM element
 * @param {boolean} flip - Whether to flip or unflip
 */
export function animateCardFlip(cardElement, flip = true) {
    if (!cardElement) return;
    
    if (flip) {
        cardElement.classList.add('flipped');
    } else {
        cardElement.classList.remove('flipped');
    }
}

/**
 * Animate card match with pulse effect
 * @param {HTMLElement} cardElement - The card DOM element
 */
export function animateCardMatch(cardElement) {
    if (!cardElement) return;
    
    cardElement.classList.add('matched');
    
    // Add pulse animation
    cardElement.style.animation = 'pulse 0.6s ease-in-out';
    
    setTimeout(() => {
        cardElement.style.animation = '';
    }, 600);
}

/**
 * Memory Game Accessibility Helpers
 */

/**
 * Generate appropriate ARIA labels for cards
 * @param {Object} card - Card object
 * @param {number} index - Card index
 * @param {number} cols - Number of columns
 * @returns {string} - ARIA label text
 */
export function generateCardAriaLabel(card, index, cols) {
    const row = Math.floor(index / cols) + 1;
    const col = (index % cols) + 1;
    const position = `row ${row}, column ${col}`;
    
    if (card.isMatched) {
        return `Matched card at ${position}`;
    } else if (card.isFlipped) {
        return `Revealed card at ${position}`;
    } else {
        return `Hidden card at ${position}`;
    }
}

/**
 * Generate game status announcements for screen readers
 * @param {Object} gameStats - Current game statistics
 * @returns {string} - Status announcement
 */
export function generateGameStatusAnnouncement(gameStats) {
    if (gameStats.isComplete) {
        return 'Congratulations! You have completed the memory game by matching all pairs.';
    } else {
        return `${gameStats.matchedPairs} of ${gameStats.totalPairs} pairs matched. ${gameStats.totalPairs - gameStats.matchedPairs} pairs remaining.`;
    }
}